import React, { useState, useEffect, useContext } from "react";
import { UserContext, API } from "@/App";
import { TYPE_CONFIG } from "@/data/gameData";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Search, Plus, Check, ArrowLeft, PenLine, Send } from "lucide-react";
import { playAddSound } from "@/data/sounds";
import axios from "axios";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";

function TypeIcon({ type, size = 64 }) {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.other;
  if (config.iconImage) {
    return <img src={config.iconImage} alt={config.label} className="rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  return (
    <div className="rounded-full flex items-center justify-center" style={{ width: size, height: size, background: config.bg, border: `3px solid ${config.color}` }}>
      <span style={{ fontSize: size * 0.45 }}>{config.emoji}</span>
    </div>
  );
}

export default function Library({ refreshStats }) {
  const { currentUser } = useContext(UserContext);
  const [view, setView] = useState("types");
  const [items, setItems] = useState([]);
  const [userItems, setUserItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [filterType, setFilterType] = useState(null);
  const [filterTag, setFilterTag] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [typeCounts, setTypeCounts] = useState([]);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customItem, setCustomItem] = useState({ title: "", author: "", content: "", type: "poetry", tags: [] });
  const [showRequestPoem, setShowRequestPoem] = useState(false);
  const [poemRequest, setPoemRequest] = useState({ title: "", author: "", content: "", type: "", category: "" });

  useEffect(() => {
    axios.get(`${API}/library/types`).then(r => setTypeCounts(r.data)).catch(() => {});
    if (currentUser) {
      axios.get(`${API}/users/${currentUser.id}/items`).then(r => setUserItems(r.data)).catch(() => {});
    }
  }, [currentUser]);

  // Fetch tags filtered by type when a type is selected
  const SONG_TAGS = ["Patriotic", "Hymns & Faith", "Folk Songs", "Children's Songs", "Nursery Rhymes", "Nature", "Other"];
  const loadTagsForType = (type) => {
    const params = type ? { type } : {};
    axios.get(`${API}/library/tags`, { params }).then(r => {
      let filtered = r.data.filter(t => t.count > 0);
      if (type === "song") {
        filtered = filtered.filter(t => SONG_TAGS.includes(t.tag));
      }
      setTags(filtered);
    }).catch(() => {});
  };

  const browseItems = (type, tag) => {
    setLoading(true);
    const params = {};
    if (type) params.type = type;
    if (tag) params.tag = tag;
    if (searchQuery) params.search = searchQuery;
    axios.get(`${API}/library`, { params })
      .then(r => { setItems(r.data); setLoading(false); setView("browse"); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (view === "browse") {
      const params = {};
      if (filterType) params.type = filterType;
      if (filterTag) params.tag = filterTag;
      if (searchQuery) params.search = searchQuery;
      axios.get(`${API}/library`, { params }).then(r => setItems(r.data)).catch(() => {});
    }
  }, [searchQuery, view, filterType, filterTag]);

  const addToCollection = async (libItem) => {
    try {
      await axios.post(`${API}/users/${currentUser.id}/add-from-library/${libItem.id}`);
      playAddSound();
      toast.success(`"${libItem.title}" added to your collection!`);
      const r = await axios.get(`${API}/users/${currentUser.id}/items`);
      setUserItems(r.data);
      if (refreshStats) refreshStats();
    } catch (e) {
      if (e.response?.status === 400) toast.info("Already in your collection!");
      else toast.error("Failed to add");
    }
  };

  const addCustomItem = async () => {
    if (!customItem.title.trim() || !customItem.content.trim()) {
      toast.error("Please enter a title and content");
      return;
    }
    try {
      // Add directly to personal collection only (not public library)
      await axios.post(`${API}/users/${currentUser.id}/items`, customItem);
      playAddSound();
      toast.success(`"${customItem.title}" added to your collection!`);
      setShowAddCustom(false);
      setCustomItem({ title: "", author: "", content: "", type: "poetry", tags: [] });
      const r = await axios.get(`${API}/users/${currentUser.id}/items`);
      setUserItems(r.data);
      if (refreshStats) refreshStats();
    } catch { toast.error("Failed to add item"); }
  };

  const submitPoemRequest = async () => {
    if (!poemRequest.title.trim()) {
      toast.error("Please enter a poem or item name");
      return;
    }
    try {
      await axios.post(`${API}/library/request`, {
        ...poemRequest,
        user_id: currentUser?.id || null
      });
      toast.success("Request submitted! Thank you.");
      setShowRequestPoem(false);
      setPoemRequest({ title: "", author: "", content: "", type: "", category: "" });
    } catch {
      toast.error("Failed to submit request");
    }
  };

  const isInCollection = (libItem) => userItems.some(ui =>
    ui.library_item_id === libItem.id || (ui.title === libItem.title && ui.author === libItem.author)
  );

  // ── Type Selection View ──
  if (view === "types") {
    return (
      <div className="slide-up" data-testid="library-page">
        <div className="open-book p-6 mb-4">
          <h2 className="font-heading text-2xl font-bold mb-2" style={{ color: "#4A3224" }}>Library</h2>
          <p className="text-sm mb-6" style={{ color: "#7A5946" }}>Choose from the collection below to add items to your treasure tome, or add your own poems and treasures you want to memorize!</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
            {Object.entries(TYPE_CONFIG).map(([type, config]) => {
              const count = typeCounts.find(t => t.type === type)?.count || 0;
              return (
                <button key={type} onClick={() => { setFilterType(type); loadTagsForType(type); setView("topics"); }}
                  className="flex flex-col items-center gap-2 hover:scale-105 transition-transform"
                  data-testid={`type-${type}`}>
                  <div className="tt-card p-3 rounded-2xl w-full flex flex-col items-center">
                    <TypeIcon type={type} size={72} />
                    <span className="font-heading text-sm font-bold mt-2" style={{ color: "#4A3224" }}>{config.label}</span>
                    <span className="text-[10px]" style={{ color: "#7A5946" }}>{count} items</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <button onClick={() => { setFilterType(null); setFilterTag(null); browseItems(null, null); }}
          className="tt-btn tt-btn-gold w-full py-3 text-sm mb-3"
          data-testid="browse-all-btn">
          Browse All Items
        </button>
        <button onClick={() => setShowAddCustom(true)}
          className="tt-btn w-full py-3 text-sm flex items-center justify-center gap-2"
          style={{ background: "white" }}
          data-testid="add-custom-btn">
          <PenLine className="w-4 h-4" /> Add a New Item
        </button>

        <button onClick={() => setShowRequestPoem(true)}
          className="tt-btn w-full py-3 text-sm flex items-center justify-center gap-2 mt-3"
          style={{ background: "#FFF8F0", border: "1px dashed #C4A882" }}
          data-testid="request-poem-btn">
          <Send className="w-4 h-4" style={{ color: "#7A5946" }} />
          <span style={{ color: "#7A5946" }}>Request a Poem for the Library</span>
        </button>

        {/* Request a Poem Dialog */}
        {showRequestPoem && (
          <Dialog open={showRequestPoem} onOpenChange={setShowRequestPoem}>
            <DialogContent className="sm:max-w-[500px] p-6 rounded-2xl" data-testid="request-poem-dialog">
              <DialogTitle className="font-heading text-xl font-bold" style={{ color: "#4A3224" }}>Request an Item</DialogTitle>
              <DialogDescription className="text-sm leading-relaxed" style={{ color: "#7A5946" }}>
                Suggest a poem, song, scripture, or other item to be added to the library for everyone. Not all requests may be added. To memorize something right away, use "Add a New Item" instead.
              </DialogDescription>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>Type</label>
                    <select value={poemRequest.type}
                      onChange={e => setPoemRequest({...poemRequest, type: e.target.value})}
                      className="w-full rounded-xl p-2.5 text-sm" style={{ border: "1px solid #D8C3B5" }}
                      data-testid="request-type">
                      <option value="">Select type...</option>
                      <option value="poetry">Poetry</option>
                      <option value="scripture">Scripture</option>
                      <option value="quote">Quote</option>
                      <option value="song">Song</option>
                      <option value="document">Document</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>Category</label>
                    <select value={poemRequest.category}
                      onChange={e => setPoemRequest({...poemRequest, category: e.target.value})}
                      className="w-full rounded-xl p-2.5 text-sm" style={{ border: "1px solid #D8C3B5" }}
                      data-testid="request-category">
                      <option value="">Select category...</option>
                      <option value="Nature">Nature</option>
                      <option value="Animals">Animals</option>
                      <option value="Faith">Faith</option>
                      <option value="Courage">Courage</option>
                      <option value="Kindness">Kindness</option>
                      <option value="Wisdom">Wisdom</option>
                      <option value="Joy">Joy</option>
                      <option value="Beauty">Beauty</option>
                      <option value="Hope">Hope</option>
                      <option value="Family">Family</option>
                      <option value="Perseverance">Perseverance</option>
                      <option value="Nursery Rhymes">Nursery Rhymes</option>
                      <option value="Holidays/Seasons">Holidays / Seasons</option>
                      <option value="Patriotism">Patriotism</option>
                      <option value="Educational">Educational</option>
                      <option value="Shakespeare">Shakespeare</option>
                      <option value="Gathering Verses">Gathering Verses</option>
                      <option value="Riddles">Riddles</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>Title *</label>
                  <Input placeholder="e.g. The Road Not Taken" value={poemRequest.title}
                    onChange={e => setPoemRequest({...poemRequest, title: e.target.value})}
                    className="rounded-xl" style={{ borderColor: "#D8C3B5" }} data-testid="request-title" />
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>Author</label>
                  <Input placeholder="e.g. Robert Frost" value={poemRequest.author}
                    onChange={e => setPoemRequest({...poemRequest, author: e.target.value})}
                    className="rounded-xl" style={{ borderColor: "#D8C3B5" }} data-testid="request-author" />
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: "#7A5946" }}>Text (optional)</label>
                  <Textarea placeholder="Paste the content here if you have it" value={poemRequest.content}
                    onChange={e => setPoemRequest({...poemRequest, content: e.target.value})}
                    className="rounded-xl min-h-[100px] text-sm" style={{ borderColor: "#D8C3B5" }}
                    data-testid="request-content" />
                </div>
                <div className="flex gap-3">
                  <button onClick={submitPoemRequest} className="tt-btn tt-btn-gold flex-1 py-2" data-testid="submit-request-btn">
                    <Send className="w-4 h-4 inline mr-1" /> Submit Request
                  </button>
                  <Button variant="ghost" onClick={() => setShowRequestPoem(false)} className="rounded-full">Cancel</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Add Custom Item Dialog */}
        {showAddCustom && (
          <Dialog open={showAddCustom} onOpenChange={setShowAddCustom}>
            <DialogContent className="sm:max-w-[500px] p-6 rounded-2xl" data-testid="add-custom-dialog">
              <DialogTitle className="font-heading text-xl font-bold" style={{ color: "#4A3224" }}>Add New Item</DialogTitle>
              <DialogDescription className="text-sm leading-relaxed" style={{ color: "#7A5946" }}>Add your own item to memorize. This will be saved to your personal collection only, not the public library. To suggest items for the public library, use "Request a Poem" instead. The public library is updated regularly on the backend.</DialogDescription>
              <div className="space-y-3 mt-3">
                <Input placeholder="Title" value={customItem.title}
                  onChange={e => setCustomItem({...customItem, title: e.target.value})}
                  className="rounded-xl" style={{ borderColor: "#D8C3B5" }} data-testid="custom-title" />
                <Input placeholder="Author (optional)" value={customItem.author}
                  onChange={e => setCustomItem({...customItem, author: e.target.value})}
                  className="rounded-xl" style={{ borderColor: "#D8C3B5" }} data-testid="custom-author" />
                <select value={customItem.type}
                  onChange={e => setCustomItem({...customItem, type: e.target.value})}
                  className="w-full rounded-xl p-2 text-sm" style={{ borderColor: "#D8C3B5", border: "1px solid #D8C3B5" }}
                  data-testid="custom-type">
                  {Object.entries(TYPE_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <textarea placeholder="Content (the text to memorize)" value={customItem.content}
                  onChange={e => setCustomItem({...customItem, content: e.target.value})}
                  className="w-full rounded-xl p-3 text-sm min-h-[120px]" style={{ borderColor: "#D8C3B5", border: "1px solid #D8C3B5" }}
                  data-testid="custom-content" />
                <div className="flex gap-3">
                  <button onClick={addCustomItem} className="tt-btn tt-btn-gold flex-1 py-2" data-testid="save-custom-btn">
                    <Plus className="w-4 h-4 inline mr-1" /> Add to Collection
                  </button>
                  <Button variant="ghost" onClick={() => setShowAddCustom(false)} className="rounded-full">Cancel</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  // ── Topic Selection View (filtered by type) ──
  if (view === "topics") {
    return (
      <div className="slide-up" data-testid="library-topics">
        <button onClick={() => setView("types")} className="flex items-center gap-2 mb-4 font-heading font-semibold"
          style={{ color: "#7A5946" }} data-testid="back-to-types">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>
        <div className="open-book p-6">
          <div className="flex items-center gap-3 mb-4">
            <TypeIcon type={filterType} size={48} />
            <div>
              <h2 className="font-heading text-xl font-bold" style={{ color: "#4A3224" }}>
                {filterType ? TYPE_CONFIG[filterType]?.label : "All"} Topics
              </h2>
              <p className="text-xs" style={{ color: "#7A5946" }}>Pick a topic to explore</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            {tags.map(tag => (
              <button key={tag.tag} onClick={() => { setFilterTag(tag.tag); browseItems(filterType, tag.tag); }}
                className="tt-card p-3 text-center hover:scale-[1.02] transition-transform"
                data-testid={`topic-${tag.tag}`}>
                <span className="font-heading text-sm font-semibold" style={{ color: "#4A3224" }}>{tag.tag}</span>
              </button>
            ))}
          </div>

          <button onClick={() => browseItems(filterType, null)}
            className="tt-btn tt-btn-ocean w-full py-2 text-sm"
            data-testid="browse-all-topics">
            Browse all {filterType ? (filterType === "song" ? "songs" : TYPE_CONFIG[filterType]?.label.toLowerCase()) : "items"}
          </button>
        </div>
      </div>
    );
  }

  // ── Browse Items View ──
  return (
    <div className="slide-up" data-testid="library-browse">
      <button onClick={() => { setView("topics"); setSearchQuery(""); }}
        className="flex items-center gap-2 mb-4 font-heading font-semibold"
        style={{ color: "#7A5946" }} data-testid="back-to-topics">
        <ArrowLeft className="w-5 h-5" /> Back
      </button>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#7A5946" }} />
        <Input className="pl-9 rounded-xl" style={{ borderColor: "#D8C3B5", background: "white" }}
          placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          data-testid="library-search" />
      </div>

      {loading ? (
        <p className="text-center py-12" style={{ color: "#7A5946" }}>Loading...</p>
      ) : items.length === 0 ? (
        <div className="open-book p-8 text-center">
          <p className="font-heading text-lg" style={{ color: "#7A5946" }}>No items found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.other;
            const added = isInCollection(item);
            return (
              <button key={item.id} onClick={() => setPreviewItem(item)}
                className="book-spine w-full text-left p-3 flex items-center gap-3"
                style={{ background: config.bg }}
                data-testid={`library-item-${item.id}`}>
                <div className="w-2 h-12 rounded-full flex-shrink-0" style={{ background: config.color }} />
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm font-semibold truncate" style={{ color: "#4A3224" }}>{item.title}</p>
                  {item.author && <p className="text-xs" style={{ color: "#7A5946" }}>by {item.author}</p>}
                </div>
                {added ? (
                  <Badge className="text-[10px] flex-shrink-0" style={{ background: "#DCFCE7", color: "#166534" }}>
                    <Check className="w-3 h-3 mr-1" /> Added
                  </Badge>
                ) : (
                  <span onClick={e => { e.stopPropagation(); addToCollection(item); }}
                    className="tt-btn tt-btn-gold px-3 py-1 text-xs flex-shrink-0"
                    data-testid={`add-${item.id}`}>
                    <Plus className="w-3 h-3 inline mr-1" /> Add
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Preview Dialog */}
      {previewItem && (
        <Dialog open={!!previewItem} onOpenChange={(o) => { if (!o) setPreviewItem(null); }}>
          <DialogContent className="sm:max-w-[600px] p-0 rounded-2xl overflow-y-auto" style={{ maxHeight: "85vh" }}
            data-testid="library-preview-dialog">
            <DialogTitle className="sr-only">{previewItem.title}</DialogTitle>
            <DialogDescription className="sr-only">{previewItem.type}</DialogDescription>
            <div className="p-6">
              <h2 className="font-heading text-xl font-bold mb-1" style={{ color: "#4A3224" }}>{previewItem.title}</h2>
              {previewItem.author && <p className="text-sm mb-3" style={{ color: "#7A5946" }}>by {previewItem.author}</p>}
              {previewItem.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {previewItem.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-[10px] rounded-full" style={{ borderColor: "#D8C3B5" }}>{tag}</Badge>
                  ))}
                </div>
              )}
              <div className="parchment rounded-xl p-4 mb-4">
                <pre className="whitespace-pre-wrap text-sm leading-7" style={{ fontFamily: "'Nunito', sans-serif", color: "#4A3224" }}
                  data-testid="preview-content">{previewItem.content}</pre>
              </div>
              <div className="flex gap-3">
                {!isInCollection(previewItem) ? (
                  <button onClick={() => { addToCollection(previewItem); setPreviewItem(null); }}
                    className="tt-btn tt-btn-gold px-6 py-2 flex items-center gap-2"
                    data-testid="preview-add-btn">
                    <Plus className="w-4 h-4" /> Add to Collection
                  </button>
                ) : (
                  <Badge className="px-4 py-2" style={{ background: "#DCFCE7", color: "#166534" }}>
                    <Check className="w-4 h-4 mr-1" /> In Your Collection
                  </Badge>
                )}
                <Button variant="ghost" onClick={() => setPreviewItem(null)} className="rounded-full">Close</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
